import { useState } from "react";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Copy, Check } from "lucide-react";

type TabKey = "criteria" | "red-flags" | "investigations" | "summaries" | "letter";

interface ReferralCriteria {
  condition: string;
  urgent: string[];
  routine: string[];
  twoww: string[];
  niceRef: string;
}

const REFERRAL_CRITERIA: ReferralCriteria[] = [
  {
    condition: "Rheumatoid Arthritis",
    twoww: [
      "Suspected inflammatory arthritis with joint swelling, warmth, or synovitis on examination in a patient aged ≥16 — refer within 3 working days per NICE CG79 early referral recommendation (not a formal 2WW cancer pathway, but equivalent urgency for early inflammatory arthritis)",
      "Note: RA does not have a NICE 2WW cancer pathway — the 3-working-day early referral standard applies instead",
    ],
    urgent: [
      "Suspected early RA with symptoms <12 weeks (refer within 3 working days per NICE CG79)",
      "Rapidly progressive polyarthritis with active synovitis",
      "Suspected septic arthritis in background of immunosuppression",
      "Acute monoarthritis in a patient established on biologic therapy (rule out septic joint)",
    ],
    routine: [
      "Polyarthritis >12 weeks with early morning stiffness and small joint involvement",
      "Positive RF or anti-CCP with compatible symptoms",
      "Established RA patient requesting specialist review from new GP",
      "Stable RA with intercurrent illness affecting treatment",
    ],
    niceRef: "NICE CG79 (Rheumatoid Arthritis in Adults)",
  },
  {
    condition: "Suspected Connective Tissue Disease (SLE, Sjögren's, Myositis, SSc)",
    twoww: [
      "No dedicated 2WW pathway for CTD — use urgent rheumatology referral for suspected systemic disease with organ involvement",
      "If malignancy is in the differential (e.g. paraneoplastic myositis with suspected ILD, rapid-onset myopathy aged >60 without prior CTD features): consider 2WW cancer of unknown primary pathway alongside rheumatology referral",
      "Suspected dermatomyositis (Gottron's papules, heliotrope rash, proximal weakness): cancer screen is mandatory — 2WW if clinical features suggest associated malignancy (especially ovarian, lung, breast, colorectal in women; lung, colorectal, bladder in men)",
    ],
    urgent: [
      "Acute lupus nephritis (new proteinuria >1+ with haematuria and renal impairment)",
      "Acute CNS manifestations (seizures, psychosis, stroke in young patient with SLE features)",
      "Acute severe myositis (rapidly progressive weakness, dysphagia, respiratory compromise)",
      "Suspected systemic vasculitis (haemoptysis, renal impairment, purpura, nerve palsy)",
    ],
    routine: [
      "Positive ANA with clinical features (malar rash, arthritis, serositis, photosensitivity)",
      "Sicca symptoms with positive anti-SSA/SSB or abnormal salivary gland function",
      "Raynaud's with suspected connective tissue disease features",
      "Unexplained proximal muscle weakness with elevated CK/aldolase",
      "Sclerodactyly, telangiectasia, or calcinosis with suspected SSc",
    ],
    niceRef: "NICE CG139 (Lupus); BSR guidelines for Sjögren's, Myositis, SSc",
  },
  {
    condition: "Axial Spondyloarthritis (axSpA / AS)",
    twoww: [
      "No dedicated 2WW cancer pathway for axSpA — however, new back pain in older patients (>50 years) without classic inflammatory features should prompt 2WW 'suspected cancer of unknown primary' or 2WW bone tumour pathway if: constant unremitting pain, night pain, unexplained weight loss, or elevated CRP/ESR disproportionate to symptom duration",
      "Spinal fracture or cord compression in known AS: direct A&E referral — do not use 2WW pathway",
    ],
    urgent: [
      "Cauda equina symptoms in context of known AS (emergency — do not wait for rheumatology)",
      "Acute spinal cord compression or myelopathy features in AS",
      "Acute uveitis referral: urgent ophthalmology (not rheumatology directly)",
    ],
    routine: [
      "Chronic low back pain onset <45 years, duration >3 months with inflammatory features (improves with exercise, not rest; night pain with getting up; good NSAIDs response)",
      "ASAS inflammatory back pain criteria met (≥4 of 5 features)",
      "Psoriasis or IBD with inflammatory back pain",
      "Elevated CRP/ESR with inflammatory back pain in young patient",
      "Family history of AS with compatible symptoms",
    ],
    niceRef: "NICE CG138 (Spondyloarthritis in over 16s)",
  },
  {
    condition: "Gout and Crystal Arthropathy",
    twoww: [
      "No dedicated 2WW pathway for gout — however, if monoarthritis in an atypical patient (e.g. young female, no metabolic syndrome, normal urate) without clear crystallographic diagnosis: consider 2WW haematology or orthopaedics if bone lesion identified on imaging",
      "Soft tissue mass mimicking tophaceous gout without confirmed MSU crystals on aspiration: 2WW soft tissue sarcoma pathway should be considered if mass is >5 cm, deep to fascia, or rapidly growing",
    ],
    urgent: [
      "Septic arthritis must be excluded — urgent A&E or same-day rheumatology if not possible in primary care",
      "Polyarticular gout flare in immunocompromised patient",
      "Tophaceous gout with rapidly progressive soft tissue or joint destruction",
    ],
    routine: [
      "Recurrent gout (≥2 attacks/year) for urate-lowering therapy initiation",
      "Elevated urate with tophi",
      "Gout in young patient (<40 years, male or female) — investigate secondary causes",
      "Gout with renal impairment requiring specialist guidance on urate-lowering",
      "Failed first-line urate-lowering therapy (allopurinol intolerance or non-response)",
    ],
    niceRef: "NICE NG219 (Gout, 2022)",
  },
  {
    condition: "Vasculitis",
    twoww: [
      "Suspected GCA in patient ≥50 with new headache, jaw claudication, or visual symptoms: same-day urgent referral — this is the highest urgency rheumatology scenario (not a 2WW pathway; requires same-day action to prevent blindness)",
      "If GCA presents with scalp necrosis, tongue or lip claudication, or anterior ischaemic optic neuropathy: 999/A&E with same-hour ophthalmology + rheumatology involvement",
      "Purpura with constitutional symptoms and age >60 without clear infection: consider 2WW haematology (myeloma, lymphoma) alongside vasculitis workup — dual referral may be appropriate",
    ],
    urgent: [
      "Suspected GCA — same-day rheumatology or urgent GCA clinic (start prednisolone 40–60 mg while awaiting, or 500 mg–1 g IV if vision threatened per BSR 2020 guideline)",
      "Suspected ANCA-associated vasculitis (haemoptysis, rapidly progressive renal failure, purpura, mononeuritis multiplex)",
      "Suspected Kawasaki disease in child — urgent paediatrics",
      "Suspected IgA vasculitis (Henoch-Schönlein Purpura) with renal involvement",
    ],
    routine: [
      "Stable cutaneous vasculitis without systemic features for investigation",
      "Positive ANCA in asymptomatic patient for baseline assessment",
    ],
    niceRef: "BSR/BHPR Guidelines for ANCA-associated vasculitis (2014); BSR GCA Guideline (2020)",
  },
  {
    condition: "Osteoporosis",
    twoww: [
      "Vertebral fracture on imaging in a patient with: unexplained weight loss, night sweats, elevated LDH/calcium, or bone lesions not consistent with osteoporotic compression — 2WW haematology (myeloma) or 2WW bone tumour pathway as appropriate",
      "New vertebral fracture in patient aged <50 without trauma or known risk factors: mandatory malignancy exclusion — 2WW pathway should be initiated alongside bone density assessment",
      "Typical osteoporotic fragility fractures without red flag features do not require 2WW — refer to Fracture Liaison Service (FLS) under routine pathway",
    ],
    urgent: [
      "Vertebral fracture in patient on long-term steroids (≥7.5 mg prednisolone ≥3 months) — urgent review",
      "Multiple vertebral fractures with acute pain (rule out malignancy)",
    ],
    routine: [
      "FRAX 10-year fracture risk above intervention threshold (NOGG 2023)",
      "T-score ≤−2.5 on DXA with or without prior fracture",
      "Fragility fracture at any site with T-score ≤−1.5 (FLS pathway)",
      "Long-term corticosteroid use (≥3 months) for bone protection assessment",
      "Secondary causes of osteoporosis requiring investigation (e.g. hyperparathyroidism, hypogonadism)",
    ],
    niceRef: "NICE TA464 (Bisphosphonates); NOGG 2023 guidelines",
  },
];

const RED_FLAGS = [
  {
    category: "Septic Arthritis",
    flags: [
      "Hot, red, swollen joint with fever and inability to weight bear",
      "Recent skin or soft tissue infection with new monoarthritis",
      "Intravenous drug use with joint pain",
      "Prosthetic joint with acute pain/swelling",
      "Any joint effusion in immunocompromised patient",
    ],
    action: "A&E IMMEDIATELY — septic arthritis is a surgical emergency. Joint aspiration and IV antibiotics required urgently.",
    colour: "red" as const,
  },
  {
    category: "Cauda Equina Syndrome",
    flags: [
      "Bilateral leg weakness or numbness",
      "Saddle anaesthesia (perineal numbness)",
      "Bladder or bowel dysfunction (retention, incontinence)",
      "Reduced anal tone",
      "In context of back pain (including AS/SpA patients)",
    ],
    action: "A&E IMMEDIATELY — MRI within 4 hours is required. Surgical emergency.",
    colour: "red" as const,
  },
  {
    category: "Giant Cell Arteritis (GCA)",
    flags: [
      "Temporal headache (new onset) age ≥50",
      "Jaw claudication",
      "Scalp tenderness",
      "Visual disturbance (amaurosis fugax, diplopia, vision loss)",
      "Elevated ESR >50 mm/hr or CRP >10 mg/L in appropriate context",
      "Ischaemic stroke with GCA features",
    ],
    action: "SAME DAY — Start prednisolone 40–60 mg (60–80 mg if visual symptoms). Refer same-day to GCA clinic or rheumatology. Ophthalmology urgently if visual symptoms.",
    colour: "red" as const,
  },
  {
    category: "Systemic Vasculitis (ANCA-associated)",
    flags: [
      "Haemoptysis with renal impairment",
      "Rapidly progressive glomerulonephritis (creatinine rising rapidly)",
      "Purpura with renal involvement or nerve palsy",
      "Mononeuritis multiplex",
      "Sinusitis with nasal crusting/ulceration and pulmonary infiltrates",
    ],
    action: "URGENT RHEUMATOLOGY — same-day/next-day referral. Do NOT delay — rapidly progressive GN can be irreversible within days.",
    colour: "orange" as const,
  },
  {
    category: "Acute Lupus Complications",
    flags: [
      "New seizures or psychosis with known/suspected SLE",
      "Rapidly worsening proteinuria with haematuria (possible lupus nephritis)",
      "Haemolytic anaemia with SLE features",
      "Acute pericarditis/pleuritis with haemodynamic compromise",
    ],
    action: "URGENT RHEUMATOLOGY or A&E depending on severity. Lupus nephritis can progress to ESRD rapidly without treatment.",
    colour: "orange" as const,
  },
  {
    category: "Acute Inflammatory Myopathy Complications",
    flags: [
      "Rapidly progressive proximal weakness over days–weeks",
      "Dysphagia with ILD features (ILD + myositis = anti-synthetase syndrome)",
      "Respiratory compromise (myositis with respiratory muscle weakness)",
      "Myalgia with CK >10× ULN",
    ],
    action: "URGENT RHEUMATOLOGY — respiratory complications may require acute admission.",
    colour: "orange" as const,
  },
];

interface Investigation {
  condition: string;
  bloods: string[];
  imaging: string[];
  other: string[];
  notes: string;
}

const INVESTIGATIONS: Investigation[] = [
  {
    condition: "Suspected RA (Polyarthritis)",
    bloods: ["FBC", "ESR, CRP", "U&E, LFTs", "Rheumatoid factor (RF)", "Anti-CCP antibody", "ANA (to exclude SLE/overlap)", "Uric acid (to exclude gout)"],
    imaging: ["X-ray hands and feet (may be normal in early RA — baseline for erosion monitoring)", "Ultrasound of affected joints if clinical uncertainty"],
    other: ["Joint aspiration if effusion present (crystal microscopy + MCS)"],
    notes: "Negative RF/anti-CCP does not exclude RA. Anti-CCP has higher specificity (~95%). Ultrasound detects synovitis more reliably than clinical examination.",
  },
  {
    condition: "Suspected SLE",
    bloods: ["FBC (pancytopenia)", "U&E, urinalysis (proteinuria/casts)", "LFTs", "ANA (titre and pattern)", "Anti-dsDNA", "Anti-Sm, anti-SSA/SSB, anti-Ro52", "Antiphospholipid panel (aCL IgG/IgM, anti-β2GP1, lupus anticoagulant)", "C3, C4 complement", "Direct Coombs test", "CRP (often normal in SLE — elevated CRP suggests infection)"],
    imaging: ["CXR (pleuritis/effusion)", "Echo if cardiac features", "Renal USS if proteinuria ≥1+"],
    other: ["Urine PCR (protein:creatinine ratio)", "24-hour urine if significant proteinuria"],
    notes: "ANA is the entry criterion for 2019 EULAR/ACR criteria. Low C3/C4 with high anti-dsDNA = active nephritis until proven otherwise.",
  },
  {
    condition: "Suspected axSpA",
    bloods: ["FBC, CRP, ESR", "HLA-B27 (supports diagnosis but not diagnostic)", "ANA, RF (to exclude other causes)", "Renal function (NSAID monitoring)", "Uric acid"],
    imaging: ["MRI sacroiliac joints (STIR sequence — most sensitive for early inflammation)", "Pelvis X-ray (plain film insensitive early but required for mNY criteria)", "Spinal X-ray if established disease to assess syndesmophytes"],
    other: ["BASMI (spinal mobility)", "BASDAI (patient-reported disease activity)"],
    notes: "HLA-B27 is present in ~90% of AS patients but found in 8% of general population — not diagnostic alone. MRI SI joints may be normal in early disease.",
  },
  {
    condition: "Suspected Gout",
    bloods: ["Serum urate (note: may be normal during acute attack — repeat when settled)", "FBC, U&E, LFTs", "Fasting glucose, lipids (metabolic syndrome)", "24-hour urine urate (if considering uricosuria)"],
    imaging: ["X-ray affected joint (late disease: punched-out erosions with sclerotic margins)", "Ultrasound (double contour sign, tophi)", "DECT if diagnostic uncertainty"],
    other: ["Joint aspiration with polarised light microscopy (gold standard) — negatively birefringent MSU crystals"],
    notes: "Serum urate is often falsely normal during an acute attack due to acute phase response. Recheck 4–6 weeks after attack resolves.",
  },
  {
    condition: "Suspected Vasculitis",
    bloods: ["FBC (anaemia, eosinophilia)", "U&E, creatinine (renal vasculitis)", "LFTs", "ESR, CRP", "ANCA (PR3 and MPO by ELISA/immunofluorescence)", "ANA, anti-dsDNA (exclude SLE)", "Complement (C3, C4)", "Anti-GBM antibody (Goodpasture's)", "Hepatitis B/C, HIV, cryoglobulins", "Serum electrophoresis"],
    imaging: ["CXR (pulmonary infiltrates, nodules, cavities)", "CT chest/abdomen/pelvis (staging, organomegaly)", "CT or MR angiography if large vessel vasculitis suspected"],
    other: ["Urinalysis (active sediment: RBC casts = glomerulonephritis)", "Urine PCR", "Nerve conduction studies if mononeuritis multiplex", "Tissue biopsy (skin, kidney, nerve, or temporal artery)"],
    notes: "ANCA positivity alone requires clinical correlation. MPO-ANCA → microscopic polyangiitis/eosinophilic GPA. PR3-ANCA → GPA. A negative ANCA does not exclude vasculitis.",
  },
];

const LETTER_TEMPLATE = `[GP Practice Letterhead]

Date: [DATE]

Dear Rheumatology Team,

Re: [Patient Full Name] | DOB: [DD/MM/YYYY] | NHS No: [XXX XXX XXXX]

RHEUMATOLOGY REFERRAL — [URGENT / ROUTINE / 2WW]

Thank you for seeing this [age]-year-old [occupation] who presents with [duration]-history of:

PRESENTING COMPLAINT:
[Describe main symptoms: joint involvement (location, symmetry, swelling, tenderness), morning stiffness duration, extra-articular features]

RELEVANT HISTORY:
• Duration of symptoms: [weeks/months]
• Impact on ADLs/work: [brief description]
• Previous joint problems: [Yes/No — detail]
• Family history of inflammatory arthritis: [Yes/No]
• Previous treatment tried: [NSAIDs/analgesia — response]

EXAMINATION FINDINGS:
• [Describe any synovitis, effusions, joint deformity, rash, lymphadenopathy, nodules]
• [Range of movement if relevant]

INVESTIGATIONS:
Blood results [Date: __ / __ / ____]:
• FBC: [values or "WNL"]
• ESR: [value] mm/hr  |  CRP: [value] mg/L
• RF: [Positive/Negative/titre]  |  Anti-CCP: [Positive/Negative/titre]
• ANA: [Positive/Negative/titre and pattern if positive]
• Uric acid: [value] mmol/L
• U&E: [values]  |  eGFR: [value]

Imaging [Date: __ / __ / ____]:
• [X-ray / USS / MRI findings — or "Not yet obtained"]

CURRENT MEDICATIONS:
• [List all medications with doses]
• Allergies: [Drug allergies / NKDA]

REASON FOR REFERRAL:
I am referring [Patient Name] because [brief clinical rationale — suspected diagnosis / disease activity / therapeutic decision / second opinion].

[If urgent: I would be grateful for an urgent appointment within [timeframe] given [specific reason].]

Please do not hesitate to contact me if you require any further information.

Yours sincerely,

Dr [Name]
GP | [Practice Name]
Tel: [number]  |  Email: [email]`;

function CriteriaSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 mb-4">
        NICE-based referral thresholds by condition. Urgent = same day to 2 weeks. Routine = up to 18 weeks per RTT pathway.
      </p>
      {REFERRAL_CRITERIA.map((c, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-sm">{c.condition}</CardTitle>
              <Badge variant="secondary" className="text-xs">{c.niceRef}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {c.twoww.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-red-700 mb-2 uppercase tracking-wide">2-Week Wait (2WW)</p>
                <ul className="space-y-1">{c.twoww.map((item, j) => (
                  <li key={j} className="text-xs text-red-600 flex gap-2"><span>•</span>{item}</li>
                ))}</ul>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wide">Urgent Referral</p>
              <ul className="space-y-1">{c.urgent.map((item, j) => (
                <li key={j} className="text-xs text-amber-700 flex gap-2"><span>•</span>{item}</li>
              ))}</ul>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Routine Referral</p>
              <ul className="space-y-1">{c.routine.map((item, j) => (
                <li key={j} className="text-xs text-slate-600 flex gap-2"><span>•</span>{item}</li>
              ))}</ul>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RedFlagSection() {
  return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800 mb-4">
        <strong>Important:</strong> Red flag symptoms warrant immediate action. Do not wait for an outpatient rheumatology appointment for the conditions listed below.
      </div>
      {RED_FLAGS.map((rf, i) => (
        <Card key={i} className={`border-l-4 ${rf.colour === "red" ? "border-l-red-500" : "border-l-amber-500"}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${rf.colour === "red" ? "text-red-500" : "text-amber-500"}`} />
              <CardTitle className="text-sm">{rf.category}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-1">
              {rf.flags.map((f, j) => (
                <li key={j} className="text-sm text-slate-700 flex items-start gap-2">
                  <span className={rf.colour === "red" ? "text-red-400" : "text-amber-400"}>•</span>
                  {f}
                </li>
              ))}
            </ul>
            <div className={`rounded-xl p-3 ${rf.colour === "red" ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"}`}>
              <p className={`text-xs font-semibold mb-1 ${rf.colour === "red" ? "text-red-700" : "text-amber-700"}`}>Required action</p>
              <p className={`text-xs ${rf.colour === "red" ? "text-red-600" : "text-amber-600"}`}>{rf.action}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function InvestigationsSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 mb-4">
        Pre-referral investigations by suspected diagnosis. Completing these prior to referral significantly reduces time to diagnosis and improves triage priority.
      </p>
      {INVESTIGATIONS.map((inv, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{inv.condition}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Bloods</p>
                <ul className="space-y-0.5">
                  {inv.bloods.map((b, j) => <li key={j} className="text-xs text-slate-600 flex gap-1"><span className="text-indigo-400">•</span>{b}</li>)}
                </ul>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Imaging</p>
                <ul className="space-y-0.5">
                  {inv.imaging.map((im, j) => <li key={j} className="text-xs text-slate-600 flex gap-1"><span className="text-indigo-400">•</span>{im}</li>)}
                </ul>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Other</p>
                <ul className="space-y-0.5">
                  {inv.other.map((o, j) => <li key={j} className="text-xs text-slate-600 flex gap-1"><span className="text-indigo-400">•</span>{o}</li>)}
                </ul>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-700"><strong>Note:</strong> {inv.notes}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function LetterSection() {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(LETTER_TEMPLATE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">
          Standard referral letter structure for rheumatology referrals. Brackets indicate fields to complete.
        </p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:border-indigo-300 hover:text-indigo-600 transition-colors"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied!" : "Copy template"}
        </button>
      </div>
      <Card>
        <CardContent className="pt-6">
          <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed bg-slate-50 rounded-xl p-4 overflow-x-auto">
            {LETTER_TEMPLATE}
          </pre>
        </CardContent>
      </Card>
      <div className="mt-4 flex gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Include all pre-referral investigations
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Specify urgency clearly
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Include current medications and allergies
        </div>
      </div>
    </div>
  );
}

interface ConditionSummary {
  condition: string;
  shortName: string;
  presenting: string[];
  examFindings: string[];
  investigations: string[];
  referralUrgency: string;
  keyMessages: string;
  redFlags: string[];
  source: string;
}

const CONDITION_SUMMARIES: ConditionSummary[] = [
  {
    condition: "Rheumatoid Arthritis",
    shortName: "RA",
    presenting: [
      "Symmetrical small joint polyarthritis (MCPJs, PIPJs, wrists, MTPs)",
      "Early morning stiffness >30 minutes",
      "Joint swelling, warmth, and synovitis",
      "Fatigue and systemic upset",
    ],
    examFindings: [
      "Synovitis: boggy swelling of MCPJs or wrists",
      "Squeeze test positive (MTP/MCP)",
      "Ulnar deviation, swan-neck or boutonnière deformity (late)",
    ],
    investigations: ["RF (positive in ~70%)", "Anti-CCP (high specificity ~95%)", "CRP/ESR", "FBC, LFTs, U&E", "X-ray hands and feet (baseline)"],
    referralUrgency: "Within 3 working days if symptoms <12 weeks (NICE CG79). Routine if >12 weeks.",
    keyMessages: "Early DMARD therapy within 3 months of symptom onset significantly improves long-term joint outcomes. Treat-to-target (DAS28 remission or low disease activity) is the standard approach.",
    redFlags: ["Monoarthritis with fever → exclude septic arthritis (A&E)", "Rapid joint destruction on imaging", "Biologic patient with acute new joint pain"],
    source: "NICE CG79 · BSR DMARD guidelines 2023",
  },
  {
    condition: "Systemic Lupus Erythematosus",
    shortName: "SLE",
    presenting: [
      "Malar (butterfly) rash, photosensitivity",
      "Oral ulcers, alopecia",
      "Arthritis/arthralgia (non-erosive)",
      "Pleuritis or pericarditis",
      "Young woman with multisystem involvement",
    ],
    examFindings: [
      "Malar rash (spares nasolabial folds)",
      "Discoid lesions",
      "Synovitis (non-deforming)",
      "Lymphadenopathy, splenomegaly",
    ],
    investigations: ["ANA (high sensitivity, entry criterion)", "Anti-dsDNA (high specificity)", "Anti-Sm, anti-SSA/Ro", "Complement C3/C4", "FBC (pancytopenia)", "U&E + urinalysis (proteinuria/haematuria)"],
    referralUrgency: "Urgent if organ involvement (renal, CNS, haematological). Routine if mild constitutional symptoms with positive serology.",
    keyMessages: "Proteinuria >1+ or haematuria with positive ANA = possible lupus nephritis — urgent nephrology/rheumatology referral. CRP often normal in SLE flare (elevated CRP suggests infection).",
    redFlags: ["New proteinuria + haematuria → urgent renal referral", "Seizures, psychosis, stroke → A&E", "Haemolytic anaemia → haematology", "Unexplained thrombosis → antiphospholipid syndrome"],
    source: "NICE CG139 · EULAR 2019 SLE recommendations",
  },
  {
    condition: "Axial Spondyloarthritis",
    shortName: "axSpA / AS",
    presenting: [
      "Chronic low back pain onset <45 years, >3 months",
      "Worse at night and in the morning, improves with exercise",
      "Good response to NSAIDs",
      "Associated features: uveitis, psoriasis, IBD",
    ],
    examFindings: [
      "Reduced lumbar spine flexion (Schober's test <5 cm)",
      "Chest expansion reduced (<5 cm)",
      "Sacroiliac joint tenderness on direct pressure",
      "Peripheral enthesitis (Achilles, plantar fascia)",
    ],
    investigations: ["HLA-B27 (positive in ~90% AS)", "CRP/ESR (may be normal in nr-axSpA)", "MRI sacroiliac joints (STIR — detects early inflammation)", "Pelvis X-ray (insensitive early)", "BASDAI score"],
    referralUrgency: "Routine if inflammatory back pain criteria met. Urgent if cauda equina symptoms (A&E directly).",
    keyMessages: "Diagnostic delay is typically 7–10 years in axSpA. NICE NG65 recommends referral for patients with inflammatory back pain and ≥1 SpA feature (HLA-B27, SI joint inflammation, IBD, uveitis, enthesitis, or family history).",
    redFlags: ["Cauda equina symptoms → A&E emergency", "Spinal fracture in AS (even minor trauma can fracture)", "New neurological deficit"],
    source: "NICE CG138 · ASAS 2016 recommendations",
  },
  {
    condition: "Gout",
    shortName: "Gout",
    presenting: [
      "Acute monoarthritis — extremely painful, rapid onset",
      "1st MTP joint (podagra) most common, then ankle/knee",
      "Erythema, warmth, swelling at affected joint",
      "Triggers: alcohol, red meat, dehydration, diuretics, surgery",
    ],
    examFindings: [
      "Exquisitely tender joint (unable to bear touch/clothing)",
      "Erythema, warmth, swelling at affected joint",
      "Tophi (chalk-white deposits) in chronic gout — ears, fingers, Achilles tendon",
    ],
    investigations: ["Serum urate (may be falsely normal during acute attack)", "Joint aspiration + polarised light microscopy (gold standard: negatively birefringent MSU crystals)", "FBC, U&E, LFTs", "Fasting glucose, lipids (metabolic syndrome)", "X-ray (late disease: punched-out erosions)"],
    referralUrgency: "Urgent if septic arthritis cannot be excluded. Routine for recurrent gout or failure of urate-lowering therapy.",
    keyMessages: "Serum urate target during urate-lowering therapy (allopurinol): <360 µmol/L (<300 if tophi). Start allopurinol 100 mg after acute attack resolves (not during flare). Colchicine or low-dose NSAID prophylaxis for first 6 months of ULT.",
    redFlags: ["Fever with joint inflammation → always exclude septic arthritis", "Atypical patient (young female, no metabolic risk) → consider pseudogout/other diagnosis", "Rapidly growing soft tissue mass → 2WW sarcoma if >5 cm, deep"],
    source: "NICE NG219 (Gout, 2022) · BSR gout guidelines 2017",
  },
  {
    condition: "Giant Cell Arteritis",
    shortName: "GCA",
    presenting: [
      "New headache (temporal, occipital) in patient ≥50 years",
      "Jaw claudication (pain chewing)",
      "Scalp tenderness",
      "Visual disturbance: amaurosis fugax, diplopia, sudden visual loss",
      "Polymyalgia rheumatica symptoms (shoulder/hip girdle pain)",
    ],
    examFindings: [
      "Tender, thickened, nodular temporal artery",
      "Absent temporal artery pulsation",
      "Jaw claudication on examination (ask patient to chew)",
      "Cranial nerve palsy if ischaemic",
    ],
    investigations: ["ESR (often >50 mm/hr)", "CRP (often >10 mg/L)", "FBC (normochromic anaemia)", "LFTs (elevated ALP common)", "Temporal artery biopsy (within 2 weeks of starting steroids)", "USS temporal arteries (alternative to biopsy in experienced centres)"],
    referralUrgency: "SAME DAY. Start prednisolone 40–60 mg immediately (do not wait for biopsy). 500mg–1g IV methylprednisolone if visual symptoms. Urgent ophthalmology if visual involvement.",
    keyMessages: "Do not wait for temporal artery biopsy before starting steroids — biopsy positive up to 2 weeks after steroids. Visual loss is irreversible if treatment is delayed. BSR 2020 fast-track GCA clinic model: same-day USS + steroid initiation reduces permanent visual loss.",
    redFlags: ["Any visual disturbance → same-hour ophthalmology", "Anterior ischaemic optic neuropathy → immediate IV steroids", "Aortic involvement → CT aortogram"],
    source: "BSR/BHPR GCA Guideline 2020 · ACR/EULAR GCA criteria 2022",
  },
  {
    condition: "Psoriatic Arthritis",
    shortName: "PsA",
    presenting: [
      "Inflammatory arthritis in patient with psoriasis",
      "Dactylitis ('sausage finger')",
      "Nail changes: pitting, onycholysis",
      "Enthesitis: Achilles, plantar fascia",
      "Oligoarticular or polyarticular patterns",
    ],
    examFindings: [
      "Dactylitis (whole-digit swelling)",
      "Psoriatic skin or nail involvement",
      "Enthesitis (localised tenderness at tendon insertions)",
      "DIP joint involvement (distinguishes from RA)",
    ],
    investigations: ["RF (usually negative — seronegative)", "Anti-CCP (usually negative)", "CRP/ESR", "X-ray affected joints (pencil-in-cup deformity in severe)", "USS joints/entheses"],
    referralUrgency: "Routine if stable skin disease with joint symptoms. Urgent if rapidly progressive joint destruction.",
    keyMessages: "NICE TA340 (secukinumab) / TA445 (ixekizumab) biologic thresholds: ≥3 tender and ≥3 swollen joints with DMARD failure. Treat skin and joint disease jointly with dermatology where possible.",
    redFlags: ["Rapidly destructive arthritis mutilans", "Severe or extensive psoriasis with joint involvement — NICE biologic eligibility"],
    source: "NICE TA340, TA445 · EULAR PsA recommendations 2019",
  },
];

function SummariesSection() {
  const [selected, setSelected] = useState<string>(CONDITION_SUMMARIES[0].shortName);
  const [copied, setCopied] = useState(false);

  const s = CONDITION_SUMMARIES.find((c) => c.shortName === selected) ?? CONDITION_SUMMARIES[0];

  const plainText = `ONE-PAGE REFERRAL SUMMARY: ${s.condition.toUpperCase()}

TYPICAL PRESENTATION
${s.presenting.map((p) => `• ${p}`).join("\n")}

EXAMINATION FINDINGS
${s.examFindings.map((e) => `• ${e}`).join("\n")}

PRE-REFERRAL INVESTIGATIONS
${s.investigations.map((i) => `• ${i}`).join("\n")}

REFERRAL URGENCY
${s.referralUrgency}

KEY CLINICAL MESSAGES
${s.keyMessages}

RED FLAGS (escalate immediately)
${s.redFlags.map((r) => `⚠ ${r}`).join("\n")}

Source: ${s.source}
Generated by RheumHub — for clinical decision support only. Does not replace individual assessment.`;

  const handleCopy = () => {
    navigator.clipboard.writeText(plainText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <p className="text-sm text-slate-600 mb-4">
        Condition-specific one-page summaries for GP referral letters. Select a condition, then copy the plain-text summary to paste into your referral system.
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        {CONDITION_SUMMARIES.map((c) => (
          <button
            key={c.shortName}
            onClick={() => setSelected(c.shortName)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              selected === c.shortName
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
            }`}
          >
            {c.shortName}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="text-base">{s.condition}</CardTitle>
            <p className="text-xs text-slate-500 mt-1">{s.source}</p>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:border-indigo-300 hover:text-indigo-600 transition-colors shrink-0"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied!" : "Copy for referral"}
          </button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Typical Presentation</p>
              <ul className="space-y-1">
                {s.presenting.map((p, i) => <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className="text-indigo-400 shrink-0">•</span>{p}</li>)}
              </ul>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Examination Findings</p>
              <ul className="space-y-1">
                {s.examFindings.map((e, i) => <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className="text-indigo-400 shrink-0">•</span>{e}</li>)}
              </ul>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Pre-referral Investigations</p>
              <ul className="space-y-1">
                {s.investigations.map((inv, i) => <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className="text-indigo-400 shrink-0">•</span>{inv}</li>)}
              </ul>
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">Referral Urgency</p>
              <p className="text-xs text-indigo-800 leading-relaxed">{s.referralUrgency}</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-amber-800 mb-1">Key clinical messages</p>
            <p className="text-xs text-amber-700 leading-relaxed">{s.keyMessages}</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Red flags — escalate immediately
            </p>
            <ul className="space-y-1">
              {s.redFlags.map((r, i) => <li key={i} className="text-xs text-red-600 flex gap-1.5"><span className="shrink-0">⚠</span>{r}</li>)}
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const TABS: { key: TabKey; label: string }[] = [
  { key: "criteria", label: "Referral Criteria" },
  { key: "red-flags", label: "Red Flags" },
  { key: "investigations", label: "Pre-referral Investigations" },
  { key: "summaries", label: "Condition Summaries" },
  { key: "letter", label: "Letter Template" },
];

export default function GpReferral() {
  const [tab, setTab] = useState<TabKey>("criteria");

  return (
    <DoctorLayout title="GP Referral Hub">
      <div className="mb-6">
        <p className="text-slate-600 mb-4">
          Referral guidance for GPs and rheumatologists producing referral criteria. Based on NICE guidelines, BSR recommendations, and NHS England frameworks.
        </p>
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                tab === t.key
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "criteria" && <CriteriaSection />}
      {tab === "red-flags" && <RedFlagSection />}
      {tab === "investigations" && <InvestigationsSection />}
      {tab === "summaries" && <SummariesSection />}
      {tab === "letter" && <LetterSection />}
    </DoctorLayout>
  );
}
